// The embed card's contents (embed.ts) as HTML, apart from the page so the
// tests can run it: a game's count and 24h change, its last 7 days as a
// sparkline, and its rank and peak.

import type { AppResponse, HistoryResponse } from "../../shared/api.ts";
import { CHANGE_MIN_PLAYERS } from "../../shared/limits.ts";
import { SPARK_H, SPARK_W, compact, sparkline } from "./chart.ts";
import { changeText, dayChange, escapeHtml, headerSrc, percentFormats } from "./web.ts";

const number = new Intl.NumberFormat();
// As the site's list rows show it.
const percent = percentFormats(undefined, "exceptZero");

// The game an embed's ?app= names: its app id, or 0 for none (missing, or
// not 1 to 10 digits).
export const embedAppId = (raw: string | null): number => (raw && /^\d{1,10}$/.test(raw) ? Number(raw) : 0);

// The name a card shows, and its page's title.
export const cardName = (game: Pick<AppResponse, "app_id" | "name">): string => game.name || `App ${game.app_id}`;

export function cardHtml(game: AppResponse, history: HistoryResponse | null): string {
  // Not for a small game: its swings are noise, and the card has no room to
  // say what from.
  const day =
    game.active && (game.day_ago_player_count ?? 0) >= CHANGE_MIN_PLAYERS ? dayChange(game.day_change) : null;
  const change = day ? `<span class="change ${day.way}">${changeText(day, percent)} <small>24h</small></span>` : "";
  const count = game.player_count == null
    ? `<span class="muted">No count yet</span>`
    : `<strong>${number.format(game.player_count)}</strong> <span class="muted">${game.active ? "playing" : "last count"}</span>`;

  const { line, area } = sparkline(history?.points ?? []);
  const chart = line
    ? `<svg class="spark" viewBox="0 0 ${SPARK_W} ${SPARK_H}" preserveAspectRatio="none" role="img" aria-label="Players over the last 7 days">
         <path class="area" d="${area}"/><path class="line" d="${line}"/></svg>`
    : `<div class="spark"></div>`;

  const facts = [
    game.rank != null ? `#${number.format(game.rank)} on Steam` : game.active ? "" : "No longer tracked",
    game.peak_player_count != null ? `Peak ${compact.format(game.peak_player_count)}` : ""
  ].filter(Boolean);

  return `
    <div class="top">
      <img src="${escapeHtml(headerSrc(game))}" alt="">
      <div class="head">
        <div class="name">${escapeHtml(cardName(game))}</div>
        <div class="count">${count}${change}</div>
      </div>
    </div>
    ${chart}
    <div class="foot">
      <span>${[...facts, "7 days"].map(escapeHtml).join(" · ")}</span>
      <span class="brand">steamdata.dev</span>
    </div>`;
}
